/*
 * Fullpage Scroll — section-by-section scroll snapping for Shopify OS 2.0.
 * Controlled by the "Fullpage Scroll" section. Hijacks wheel / touch / keyboard
 * on desktop and animates the document to snap onto full-viewport sections.
 * Below the configured breakpoint it stands down and native scrolling resumes.
 */
(function () {
  'use strict';

  var EASE = function (t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  function bool(v, fallback) {
    if (v === undefined || v === null || v === '') return fallback;
    return v === 'true' || v === true;
  }

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  class FullpageScroll extends HTMLElement {
    constructor() {
      super();
      this.panels = [];
      this.index = 0;
      this.animating = false;
      this.enabled = false;
      this.touchStartY = null;

      this._onWheel = this._onWheel.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onAnchorClick = this._onAnchorClick.bind(this);
    }

    connectedCallback() {
      var d = this.dataset;
      this.cfg = {
        container: d.container || 'MainContent',
        breakpoint: num(d.breakpoint, 768),
        duration: num(d.duration, 800),
        cooldown: num(d.cooldown, 150),
        wheelThreshold: num(d.wheelThreshold, 8),
        touchThreshold: num(d.touchThreshold, 50),
        minPanelHeight: num(d.minPanelHeight, 80),
        keyboard: bool(d.keyboard, true),
        loop: bool(d.loop, false),
        dots: bool(d.dots, true),
        dotLabels: bool(d.dotLabels, false),
        includeFooter: bool(d.includeFooter, true),
        offsetHeader: bool(d.offsetHeader, true),
        alignCenter: (d.align || 'center') === 'center',
        updateHash: bool(d.updateHash, false),
        scrollOffset: num(d.scrollOffset, 0)
      };

      this.panelIds = this._parseIdList(d.panelIds);
      this.panelTitles = this._parseIdList(d.panelTitles);
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.main = document.getElementById(this.cfg.container) || document.body;
      this.dotsNav = this.querySelector('[data-fp-dots]');

      // Stay completely inert inside the Theme Editor so sections render and
      // scroll normally while editing.
      if (this.designMode) return;

      this.refresh();

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      document.addEventListener('click', this._onAnchorClick);
      this._bindDesignMode();

      // Recompute once late assets (images, fonts) settle heights.
      window.addEventListener('load', this._onResize);

      // Honor a hash present on initial load once panels/ids are ready.
      if (!this.designMode) this._goToHash(location.hash, true);
    }

    disconnectedCallback() {
      this._teardownInput();
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onResize);
      document.removeEventListener('click', this._onAnchorClick);
    }

    /* ---------- setup ---------- */

    refresh() {
      this._collectPanels();
      this._assignIds();
      this._measure();
      this._buildDots();
      this._applyMode();
      this._syncActiveFromScroll();
    }

    _collectPanels() {
      var self = this;
      var all = Array.prototype.slice.call(
        this.main.querySelectorAll(':scope > .shopify-section')
      );
      all.forEach(function (el) {
        el.classList.remove('fp-merged', 'fp-lead');
        el.style.removeProperty('--fp-merge-space');
      });
      this.panels = [];

      all.forEach(function (el) {
        if (el.classList.contains('fp-controller')) return;

        // Sections flagged to merge join the previous panel instead of
        // becoming their own; the lead panel then hugs its own content.
        if (el.querySelector('[data-fp-merge]') && self.panels.length > 0) {
          var lead = self.panels[self.panels.length - 1];
          el.classList.add('fp-merged');
          el.classList.remove('fp-panel', 'fp-skip');
          lead.classList.add('fp-lead');
          return;
        }

        if (el.offsetHeight < self.cfg.minPanelHeight) {
          el.classList.add('fp-skip');
          el.classList.remove('fp-panel');
          return;
        }
        el.classList.remove('fp-skip');
        el.classList.add('fp-panel');
        self.panels.push(el);
      });

      if (this.cfg.includeFooter) {
        var footer = document.querySelector('.shopify-section-group-footer-group');
        if (footer) {
          Array.prototype.slice
            .call(footer.querySelectorAll(':scope > .shopify-section'))
            .forEach(function (el) {
              el.classList.add('fp-panel', 'fp-panel--natural');
              self.panels.push(el);
            });
        }
      }
    }

    _parseIdList(raw) {
      if (!raw) return [];
      var text = raw;
      try { text = JSON.parse(raw); } catch (e) { /* already plain */ }
      return String(text).split(/\r?\n/).map(function (s) { return s.trim(); });
    }

    _slug(str) {
      return String(str || '')
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    // Gives every panel a clean, mappable anchor id via a zero-height child div.
    // Priority: editor override list > in-markup data-fp-id > slug of the label.
    _assignIds() {
      var self = this;
      var used = {};
      this.panels.forEach(function (panel, i) {
        var override = self.panelIds[i];
        var marker = panel.querySelector('[data-fp-id]');
        var inMarkup = marker && marker.getAttribute('data-fp-id');
        var base = override || inMarkup || self._slug(self._labelFor(panel, i)) || ('section-' + (i + 1));

        var id = base;
        var n = 2;
        while (used[id]) { id = base + '-' + n++; }
        used[id] = true;

        var anchor = panel.querySelector(':scope > .fp-anchor');
        if (!anchor) {
          anchor = document.createElement('div');
          anchor.className = 'fp-anchor';
          panel.insertBefore(anchor, panel.firstChild);
        }
        anchor.id = id;
        panel.setAttribute('data-fp-id', id);
        panel.__fpId = id;
      });
    }

    /* ---------- anchor links ---------- */

    _panelIndexForId(id) {
      if (!id) return -1;
      for (var i = 0; i < this.panels.length; i++) {
        if (this.panels[i].__fpId === id) return i;
      }
      // Fall back to a panel that contains an element with that id.
      var el = document.getElementById(id);
      if (el) {
        for (var j = 0; j < this.panels.length; j++) {
          if (this.panels[j].contains(el)) return j;
        }
      }
      return -1;
    }

    _hashFromLink(link) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) === '#') return href.slice(1);
      try {
        var u = new URL(link.href, location.href);
        if (u.pathname === location.pathname && u.hash) return u.hash.slice(1);
      } catch (e) { /* ignore */ }
      return null;
    }

    _goToHash(hash, immediate) {
      if (!hash) return false;
      var id = decodeURIComponent(hash.replace(/^#/, ''));
      var idx = this._panelIndexForId(id);
      if (idx === -1) return false;
      var self = this;
      var run = function () { self.goTo(idx, { user: true, force: true }); };
      if (immediate) requestAnimationFrame(run);
      else run();
      return true;
    }

    _onAnchorClick(e) {
      if (this.designMode) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest && e.target.closest('a[href]');
      if (!link || link.target === '_blank') return;

      var hash = this._hashFromLink(link);
      if (!hash) return;
      var id = decodeURIComponent(hash.replace(/^#/, ''));
      // Only hijack links that point at a panel's own id, so in-section
      // anchor controls (accordions, tabs) keep working normally.
      var idx = -1;
      for (var i = 0; i < this.panels.length; i++) {
        if (this.panels[i].__fpId === id) { idx = i; break; }
      }
      if (idx === -1) return;

      e.preventDefault();
      this.goTo(idx, { user: true, force: true });
      if (!this.designMode) {
        try { history.pushState(null, '', '#' + id); } catch (err) { /* ignore */ }
      }
    }

    _measure() {
      var headerOffset = 0;
      if (this.cfg.offsetHeader) {
        // Match the theme's custom header detection (see rs-feature-columns).
        var header = document.querySelector('.rsx-custom-header-wrap') ||
          document.querySelector('sticky-header.header-wrapper') ||
          document.querySelector('sticky-header, .header-wrapper');
        if (header) {
          var pos = window.getComputedStyle(header).position;
          if (pos === 'sticky' || pos === 'fixed') {
            headerOffset = header.getBoundingClientRect().height;
          }
        }
      }
      // CSS panel sizing uses the header height; the scroll target adds the
      // manual nudge so merchants can fine-tune the landing position.
      this.offset = Math.max(0, headerOffset + this.cfg.scrollOffset);
      document.documentElement.style.setProperty('--fp-offset', headerOffset + 'px');
      document.documentElement.style.setProperty('--fp-vh', window.innerHeight + 'px');
    }

    _buildDots() {
      if (!this.dotsNav || !this.cfg.dots) return;
      var self = this;
      this.dotsNav.innerHTML = '';

      this.panels.forEach(function (panel, i) {
        var label = self._labelFor(panel, i);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fp-dots__dot';
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
        if (panel.__fpId) btn.dataset.fpTarget = '#' + panel.__fpId;
        if (i === self.index) btn.setAttribute('aria-current', 'true');

        var span = document.createElement('span');
        span.className = 'fp-dots__label';
        span.textContent = label;
        btn.appendChild(span);

        btn.addEventListener('click', function () {
          self.goTo(i, { user: true });
        });
        self.dotsNav.appendChild(btn);
      });
      this.dots = Array.prototype.slice.call(this.dotsNav.children);
    }

    _labelFor(panel, i) {
      var custom = this.panelTitles && this.panelTitles[i];
      if (custom) return custom;
      if (panel.dataset.fpLabel) return panel.dataset.fpLabel;
      var heading = panel.querySelector('h1, h2, h3, [role="heading"]');
      var text = heading ? heading.textContent.trim().replace(/\s+/g, ' ') : '';
      if (text) return text.length > 40 ? text.slice(0, 39) + '…' : text;
      return 'Section ' + (i + 1);
    }

    /* ---------- mode / breakpoint ---------- */

    _applyMode() {
      var wide = window.innerWidth >= this.cfg.breakpoint;
      var design = window.Shopify && window.Shopify.designMode;
      var shouldEnable = wide && this.panels.length > 1;

      document.documentElement.classList.toggle('fp-enabled', shouldEnable);
      document.documentElement.classList.toggle('fp-align-center', shouldEnable && this.cfg.alignCenter);
      if (this.dotsNav) this.dotsNav.hidden = !this.cfg.dots || !shouldEnable;

      // Input hijack: on for desktop, off in the editor so sections stay editable.
      var hijack = shouldEnable && !design;
      if (hijack && !this.enabled) this._setupInput();
      if (!hijack && this.enabled) this._teardownInput();
      this.enabled = hijack;
    }

    _setupInput() {
      window.addEventListener('wheel', this._onWheel, { passive: false });
      window.addEventListener('touchstart', this._onTouchStart, { passive: true });
      window.addEventListener('touchmove', this._onTouchMove, { passive: false });
      window.addEventListener('touchend', this._onTouchEnd, { passive: true });
      if (this.cfg.keyboard) window.addEventListener('keydown', this._onKey);
    }

    _teardownInput() {
      window.removeEventListener('wheel', this._onWheel);
      window.removeEventListener('touchstart', this._onTouchStart);
      window.removeEventListener('touchmove', this._onTouchMove);
      window.removeEventListener('touchend', this._onTouchEnd);
      window.removeEventListener('keydown', this._onKey);
    }

    /* ---------- navigation ---------- */

    _targetY(panel) {
      var top = panel.getBoundingClientRect().top + window.scrollY;
      return Math.max(0, Math.round(top - this.offset));
    }

    goTo(i, opts) {
      opts = opts || {};
      var last = this.panels.length - 1;

      if (this.cfg.loop) {
        if (i < 0) i = last;
        else if (i > last) i = 0;
      } else {
        i = Math.max(0, Math.min(last, i));
      }
      if (!this.panels[i]) return;
      if (i === this.index && !opts.force) {
        return;
      }

      this.index = i;
      this._setActive(i);
      this.animating = true;

      var panel = this.panels[i];
      if (this.cfg.updateHash && !this.designMode && panel.__fpId) {
        try {
          history.replaceState(null, '', '#' + panel.__fpId);
        } catch (e) { /* ignore */ }
      }

      var self = this;
      this._animateTo(this._targetY(panel), function () {
        clearTimeout(self._cool);
        self._cool = setTimeout(function () {
          self.animating = false;
        }, self.cfg.cooldown);
      });
    }

    _animateTo(y, done) {
      if (this._raf) cancelAnimationFrame(this._raf);

      if (this.reduceMotion || this.cfg.duration <= 0) {
        window.scrollTo(0, y);
        if (done) done();
        return;
      }

      var start = window.scrollY;
      var dist = y - start;
      var dur = this.cfg.duration;
      var t0 = null;
      var self = this;

      var step = function (ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        window.scrollTo(0, start + dist * EASE(p));
        if (p < 1) {
          self._raf = requestAnimationFrame(step);
        } else {
          self._raf = null;
          if (done) done();
        }
      };
      this._raf = requestAnimationFrame(step);
    }

    _setActive(i) {
      if (!this.dots) return;
      this.dots.forEach(function (dot, idx) {
        if (idx === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* ---------- input handlers ---------- */

    _onWheel(e) {
      if (!this.enabled) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      var dir = e.deltaY > 0 ? 1 : -1;
      var last = this.panels.length - 1;
      // Release the hijack at the edges so users can reach anything above
      // the first panel or below the last one (e.g. the footer).
      if (!this.cfg.loop) {
        if ((this.index === 0 && dir < 0) || (this.index === last && dir > 0)) {
          return;
        }
      }

      e.preventDefault();
      if (this.animating) return;
      if (Math.abs(e.deltaY) < this.cfg.wheelThreshold) return;
      this.goTo(this.index + dir);
    }

    _onKey(e) {
      if (!this.enabled) return;
      var el = e.target;
      if (el && (el.matches('input, textarea, select, [contenteditable="true"]') ||
        el.isContentEditable)) return;

      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
          e.preventDefault();
          this.goTo(this.index + 1);
          break;
        case ' ':
          if (!e.shiftKey) { e.preventDefault(); this.goTo(this.index + 1); }
          else { e.preventDefault(); this.goTo(this.index - 1); }
          break;
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          this.goTo(this.index - 1);
          break;
        case 'Home':
          e.preventDefault();
          this.goTo(0);
          break;
        case 'End':
          e.preventDefault();
          this.goTo(this.panels.length - 1);
          break;
      }
    }

    _onTouchStart(e) {
      if (!this.enabled) return;
      this.touchStartY = e.touches[0].clientY;
    }

    _onTouchMove(e) {
      if (!this.enabled || this.touchStartY === null) return;
      var dy = this.touchStartY - e.touches[0].clientY;
      var last = this.panels.length - 1;
      if (!this.cfg.loop) {
        if ((this.index === 0 && dy < 0) || (this.index === last && dy > 0)) return;
      }
      // Block native scroll while a gesture is being interpreted as a snap.
      if (Math.abs(dy) > 4) e.preventDefault();
    }

    _onTouchEnd(e) {
      if (!this.enabled || this.touchStartY === null) return;
      var endY = (e.changedTouches && e.changedTouches[0].clientY) || this.touchStartY;
      var dy = this.touchStartY - endY;
      this.touchStartY = null;
      if (this.animating) return;
      if (Math.abs(dy) < this.cfg.touchThreshold) return;
      this.goTo(this.index + (dy > 0 ? 1 : -1));
    }

    /* ---------- passive tracking ---------- */

    _onScroll() {
      if (this.animating) return;
      if (this._scrollRaf) return;
      var self = this;
      this._scrollRaf = requestAnimationFrame(function () {
        self._scrollRaf = null;
        self._syncActiveFromScroll();
      });
    }

    _syncActiveFromScroll() {
      if (!this.panels.length) return;
      var marker = window.scrollY + this.offset + window.innerHeight * 0.35;
      var active = 0;
      for (var i = 0; i < this.panels.length; i++) {
        if (this.panels[i].getBoundingClientRect().top + window.scrollY <= marker) {
          active = i;
        }
      }
      if (active !== this.index) {
        this.index = active;
        this._setActive(active);
      }
    }

    _onResize() {
      clearTimeout(this._resizeT);
      var self = this;
      this._resizeT = setTimeout(function () {
        self.refresh();
      }, 150);
    }

    /* ---------- theme editor ---------- */

    _bindDesignMode() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      var self = this;
      document.addEventListener('shopify:section:load', function () { self.refresh(); });
      document.addEventListener('shopify:section:unload', function () { self.refresh(); });
      document.addEventListener('shopify:section:select', function (evt) {
        var section = evt.target;
        var idx = self.panels.indexOf(section);
        if (idx === -1 && section) {
          var panel = section.closest && section.closest('.fp-panel');
          idx = panel ? self.panels.indexOf(panel) : -1;
        }
        if (idx > -1) {
          window.scrollTo({ top: self._targetY(self.panels[idx]), behavior: 'smooth' });
          self.index = idx;
          self._setActive(idx);
        }
      });
    }
  }

  if (!customElements.get('fullpage-scroll')) {
    customElements.define('fullpage-scroll', FullpageScroll);
  }
})();
